import React from "react";
import { Box, Tab, TabList, Tabs, TabPanel } from "@mui/joy";
import { Assignment } from "../../../api/assignments/Assignments.domain";
import { useAssignments } from "../../../contexts/controllers/useAssignments";
import {
  AssignmentAcceptanceStatus,
  MissionStatus,
} from "../../../api/assignments/Assignments.codes";
import { LoadingState } from "./AssignmentsTabsLoadingState";
import { EmptyState } from "./AssignmentsTabsEmptyState";
import { AssignmentsList } from "./AssignmentsList";
import { PaginationBar } from "./AssignmentsTabsPagination";

type TabStatusConfig = {
  label: string;
  status: AssignmentAcceptanceStatus;
  availableMissionStatuses: { label: string; status: MissionStatus }[];
  defaultMissionStatus: number;
};

const PAGE_SIZE_OPTIONS = [10, 20, 50] as const;

const AssignmentsTabs: React.FC = () => {
  const { getAssignmentsByAcceptanceStatus, pagination } = useAssignments();
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [pageSize, setPageSize] = React.useState<number>(10);
  const [value, setValue] = React.useState<number>(0);
  const [missionStatusValue, setMissionStatusValue] = React.useState<number>(0);
  const [assignments, setAssignments] = React.useState<Assignment[] | null>(null);
  const [loading, setLoading] = React.useState<boolean>(false);

  const tabStatuses: TabStatusConfig[] = [
    {
      label: "En attente",
      status: AssignmentAcceptanceStatus.UNAVAILABLE,
      availableMissionStatuses: [{ label: "En attente", status: MissionStatus.PENDING }],
      defaultMissionStatus: 0,
    },
    {
      label: "Acceptés",
      status: AssignmentAcceptanceStatus.ACCEPTED,
      availableMissionStatuses: [
        { label: "En cours", status: MissionStatus.IN_PROGRESS },
        { label: "Terminés", status: MissionStatus.DONE },
      ],
      defaultMissionStatus: 0,
    },
    {
      label: "Refusés",
      status: AssignmentAcceptanceStatus.REJECTED,
      availableMissionStatuses: [{ label: "En attente", status: MissionStatus.PENDING }],
      defaultMissionStatus: 0,
    },
  ];

  const getCurrentMissionStatus = () => {
    return tabStatuses[value].availableMissionStatuses[missionStatusValue].status;
  };

  const fetchAssignments = async () => {
    setLoading(true);
    try {
      const result = await getAssignmentsByAcceptanceStatus(
        tabStatuses[value].status,
        getCurrentMissionStatus(),
        currentPage,
        pageSize,
      );
      setAssignments(result?.response || []);
    } catch (error) {
      console.error(
        `Erreur lors de la récupération des assignments pour ${tabStatuses[value].status}:`,
        error,
      );
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    setCurrentPage(1);
    setMissionStatusValue(tabStatuses[value].defaultMissionStatus);
  }, [value]);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [missionStatusValue]);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  React.useEffect(() => {
    fetchAssignments();
  }, [value, missionStatusValue, currentPage, pageSize]);

  const handleChange = (event: React.SyntheticEvent | null, newValue: string | number | null) => {
    setValue(newValue as number);
  };

  const handleMissionStatusChange = (
    event: React.SyntheticEvent | null,
    newValue: string | number | null,
  ) => {
    setMissionStatusValue(newValue as number);
  };

  const handleActionComplete = () => {
    fetchAssignments();
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (_: React.SyntheticEvent | null, newSize: number | null) => {
    if (newSize != null) setPageSize(newSize);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: "1 1 0",
        minHeight: 0,
        overflow: "hidden",
        backgroundColor: "background.surface",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flex: "1 1 0",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Tabs
          orientation="vertical"
          value={value}
          onChange={handleChange}
          sx={{
            display: "flex",
            flex: "1 1 0",
            minHeight: 0,
            overflow: "hidden",
            width: "100%",
            columnGap: 2,
          }}
        >
          <TabList
            sx={{
              width: "clamp(120px, 18vw, 180px)",
              borderRight: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.surface",
              flexShrink: 0,
              py: 1,
              px: 1,
            }}
          >
            {tabStatuses.map((tab, index) => (
              <Tab
                key={index}
                value={index}
                sx={{
                  color: "neutral.900",
                  "&:hover": {
                    backgroundColor: "neutral.softBg",
                    color: "neutral.900",
                  },
                  "&.Mui-selected": {
                    color: "primary.700",
                    backgroundColor: "primary.softBg",
                    fontWeight: "bold",
                  },
                }}
              >
                {tab.label}
              </Tab>
            ))}
          </TabList>

          <TabPanel
            value={value}
            sx={{
              flex: "1 1 0",
              minHeight: 0,
              overflow: "hidden",
              p: 0,
              m: 0,
              backgroundColor: "background.body",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {tabStatuses[value].availableMissionStatuses.length > 1 && (
              <Box
                sx={{
                  flexShrink: 0,
                  backgroundColor: "background.surface",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  px: 2,
                  py: 1,
                }}
              >
                <Tabs
                  value={missionStatusValue}
                  onChange={handleMissionStatusChange}
                  sx={{ minHeight: "auto" }}
                >
                  <TabList
                    sx={{
                      backgroundColor: "transparent",
                      minHeight: "auto",
                      "--TabList-gap": "8px",
                    }}
                  >
                    {tabStatuses[value].availableMissionStatuses.map((missionStatus, index) => (
                      <Tab
                        key={index}
                        value={index}
                        sx={{
                          minHeight: "32px",
                          fontSize: "0.875rem",
                          color: "neutral.600",
                          "&:hover": {
                            backgroundColor: "neutral.softBg",
                            color: "neutral.700",
                          },
                          "&.Mui-selected": {
                            color: "primary.700",
                            backgroundColor: "primary.softBg",
                            fontWeight: "bold",
                          },
                        }}
                      >
                        {missionStatus.label}
                      </Tab>
                    ))}
                  </TabList>
                </Tabs>
              </Box>
            )}

            <Box
              sx={{
                flex: "1 1 0",
                minHeight: 0,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  flex: "1 1 0",
                  minHeight: 0,
                  overflowX: "hidden",
                  overflowY: "scroll",
                  width: "100%",
                  px: 2,
                  py: 2,
                }}
              >
                {loading ? (
                  <LoadingState />
                ) : assignments && assignments.length > 0 ? (
                  <AssignmentsList
                    assignments={assignments}
                    showAcceptanceActions={
                      tabStatuses[value].status === AssignmentAcceptanceStatus.UNAVAILABLE
                    }
                    editMode={tabStatuses[value].status === AssignmentAcceptanceStatus.ACCEPTED}
                    onActionComplete={handleActionComplete}
                  />
                ) : (
                  <EmptyState
                    tabLabel={tabStatuses[value].label}
                    missionStatusLabel={
                      tabStatuses[value].availableMissionStatuses.length > 1
                        ? tabStatuses[value].availableMissionStatuses[missionStatusValue].label
                        : undefined
                    }
                  />
                )}
              </Box>

              <PaginationBar
                pagination={pagination}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                pageSize={pageSize}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
              />
            </Box>
          </TabPanel>
        </Tabs>
      </Box>
    </Box>
  );
};

export default AssignmentsTabs;
